<template>
  <CBox>
    <Header :showLogo="true" />
    <omint-page-content class="verification-content">
      <OmtTypography mb="8px" as="h1-bold"> Verificação em duas etapas </OmtTypography>
      <OmtTypography mb="24px" as="h3-semi-bold"> Insira o código que enviamos para: </OmtTypography>
      <CBox bg="#F6F6F9" p="16px"
mb="24px">
        <CFlex mb="16px" alignItems="center">
          <OmtIcons mr="16px" size="lg"
name="address-card" />
          <OmtTypography as="h2-bold">Contatos</OmtTypography></CFlex
        >
        <CBox v-if="this.mfa_email" mb="8px">
          <OmtTypography color="var(--colors-light-primary-gray2)" as="p2">E-mail </OmtTypography>
          <OmtTypography as="p1"> {{ this.mfa_email }} </OmtTypography></CBox
        >
        <CBox v-if="this.mfa_telefone">
          <OmtTypography color="var(--colors-light-primary-gray2)" as="p2">
            SMS para o celular com o final</OmtTypography
          >
          <OmtTypography as="p1"> {{ this.mfa_telefone }} </OmtTypography></CBox
        >
        <CDivider borderWidth="0.5px" />
        <OmtTypography mb="16px" color="var(--colors-light-primary-gray2)"
as="p2">
          Caso o código não chegue em até 10 minutos verifique sua caixa de spam ou clique abaixo:
        </OmtTypography>
        <CFlex justifyContent="center">
          <OmtButton
            color="var(--colors-light-primary-navy-blue)"
            variant="ghost"
            p="0"
            height="auto"
            fontSize="14px"
            fontWeight="700"
            @click="login(true)"
          >
            Reenviar código
          </OmtButton></CFlex
        >
      </CBox>

      <CBox pb="24px">
        <OmtTypography color="var(--colors-light-primary-gray2)" as="p2"> Código de confirmação </OmtTypography>
        <OmtInput
          v-model="mfa_code"
          v-mask="'######'"
          :height="27"
          fontSize="14px"
          fontWeight="700"
          type="number"
          :class="{ 'input-fixed': (iOS || isAndroid) && inputFocus }"
          :style="(iOS || isAndroid) && inputFocus ? inputFixedStyle : null"
          @focus="inputFocus = true"
          @blur="inputFocus = false"
        />
      </CBox>

      <FixedButtonRow :useFooter="false">
        <OmtButton
          :w="['100%', '308px !important']"
          height="58px"
          mt="16px"
          :disabled="!isMfaCodeValid"
          @click="login(false)"
        >
          Validar
        </OmtButton>
      </FixedButtonRow>
    </omint-page-content>
  </CBox>
</template>

<script>
import FixedButtonRow from "@/components/Atoms/FixedButtonRow/index.vue";
import Header from "@/components/Molecules/Header/index.vue";
import { ROTA_BASE, ROTA_PADRAO, ROTA_VINCULOS, ROTA_OPTIN } from "@/utils/perfilRouteUtil";
import { mapActions, mapMutations, mapState } from "vuex";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

export default {
  components: {
    FixedButtonRow,
    Header
  },
  data() {
    return {
      mfa_code: "",
      msgErro: "",
      inputFocus: false
    };
  },
  computed: {
    ...mapState("biometrics", ["shoudUseBiometric"]),
    ...mapState("saude", [
      "usernameMFA",
      "passwordMFA",
      "mfa_key",
      "mfa_email",
      "mfa_telefone",
      "typeLogin",
      "fallbackUsernameMFA",
      "fallbackPasswordMFA",
      "fallbackTypeLoginMFA"
    ]),
    hasValidNextUrl() {
      return (
        this.$route.query.nextUrl &&
        this.$route.query.nextUrl !== ROTA_BASE &&
        this.$route.query.nextUrl !== ROTA_PADRAO &&
        this.$route.query.nextUrl !== ROTA_OPTIN &&
        this.$route.query.nextUrl !== ROTA_VINCULOS
      );
    },
    username() {
      return this.usernameMFA ?? this.fallbackUsernameMFA ?? this.typeLogin ?? this.fallbackTypeLoginMFA;
    },
    password() {
      return this.passwordMFA ?? this.fallbackPasswordMFA;
    },
    iOS() {
      return navigator.userAgent.match(/iPhone|iPad|iPod/i);
    },
    isAndroid() {
      return navigator.userAgent.match(/Android/i);
    },
    isMfaCodeValid() {
      return `${this.mfa_code ?? ""}`.trim().length === 6;
    },
    inputFixedStyle() {
      return {
        position: "fixed",
        left: "16px",
        right: "16px",
        width: "100%",
        bottom: "calc(env(safe-area-inset-bottom) + 74px)",
        zIndex: 2
      };
    }
  },
  mounted() {
    if (!this.mfa_key) {
      this.$router.replace(ROTA_BASE);
    }
  },
  methods: {
    ...mapActions("saude", ["loginUser"]),
    ...mapMutations("saude", ["SET_MFA_INFOS", "CLEAR_MFA_INFOS"]),
    ...mapActions("biometrics", [
      "authenticateUser",
      "registerListeners",
      "setAuthenticated",
      "setUserAuthenticationState"
    ]),
    ...mapMutations("saude", ["LOGOUT_USER"]),
    buildLoginPayload(resetCode) {
      return {
        username: this.username,
        password: this.password,
        mfa_code: resetCode ? "" : `${this.mfa_code ?? ""}`.trim(),
        mfa_key: resetCode ? "" : this.mfa_key
      };
    },
    handleLoginError(error, resetCode) {
      const msg = error.message;
      this.msgErro = msg;

      if (!resetCode) {
        Swal.fire({
          icon: "error",
          text: "O código informado está incorreto. Por favor, verifique os dados e tente novamente."
        });
      }
      this.getInfosMFA(error.response?.headers);
    },
    async redirectToInternalRoute(nextRoute) {
      if (!this.hasValidNextUrl) {
        await this.$router.replace(nextRoute);
        return;
      }
      if (nextRoute === ROTA_VINCULOS || nextRoute === ROTA_OPTIN) {
        await this.$router.replace({
          path: nextRoute,
          query: {
            nextUrl: this.$route.query.nextUrl
          }
        });
        return;
      }
      await this.$router.replace(this.$route.query.nextUrl);
    },
    async redirectAuthenticatedUser(nextRoute) {
      this.setUserAuthenticationState(true);
      if (this.shoudUseBiometric) {
        this.setAuthenticated();
      }
      try {
        if (nextRoute.startsWith("/")) {
          await this.redirectToInternalRoute(nextRoute);
          return;
        }
        await this.LOGOUT_USER();
        globalThis.window.location = nextRoute;
      } catch (error) {
        this.handleRedirectError(error);
      }
    },
    handleRedirectError(error) {
      const msg = error.message;
      if (msg.includes("Redirected when going from") || msg.includes("Navigation cancelled")) {
        this.$router.replace(this.$route.query.nextUrl);
        return;
      }
      console.error("Erro no redirect:", error);
    },
    async login(resetCode) {
      let nextRoute;
      if (!resetCode && !this.isMfaCodeValid) return;
      if (!navigator.onLine) {
        globalThis.window.dispatchEvent(new Event("offline"));
        return;
      }
      try {
        this.$root.$emit("show-loading");
        nextRoute = await this.loginUser(this.buildLoginPayload(resetCode));

        // Faz o redirect para a rota de origem (nextUrl) logo após a validação de MFA
        if (this.$route.query.nextUrl) {
          setTimeout(() => {
            this.$router.replace(this.$route.query.nextUrl);
          }, 1000);
        }

        this.CLEAR_MFA_INFOS();
      } catch (error) {
        this.handleLoginError(error, resetCode);
      } finally {
        this.$root.$emit("hide-loading");
      }

      if (nextRoute !== "" && nextRoute !== ROTA_PADRAO) await this.redirectAuthenticatedUser(nextRoute);
    },
    getInfosMFA(resp) {
      const celular = this.mfa_telefone;
      const email = this.mfa_email;
      const username = this.username;
      const password = this.password;

      this.SET_MFA_INFOS({
        mfa_key: resp["x-mfa-key"],
        mfa_email: email,
        username: username,
        password: password,
        telefone: celular
      });
    }
  }
};
</script>

<style scoped>
.verification-content {
  min-height: calc(100dvh - 120px);
  padding-bottom: calc(84px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.input-fixed {
  background: #fff;
}

@supports not (padding-bottom: env(safe-area-inset-bottom)) {
  .verification-content {
    padding-bottom: 84px;
  }
}
</style>
